import { Component, Input, Output, EventEmitter } from '@angular/core';
import { DownloadButtonIcons, DownloadStatus, DownloadStatusLabels } from '../../types/download-button-types';

/** A reusable button component for initiating downloads, displaying different labels and icons based on the download status */
@Component({
    selector: 'download-button',
    templateUrl: './download-button.component.html',
    styleUrls: ['./download-button.component.scss'],
    standalone: false
})
export class DownloadButtonComponent {
  /** Current status of the download process */
  @Input() status!: DownloadStatus;
  /** Custom labels for each download status */
  @Input() labels?: DownloadStatusLabels;
  /** Custom icons for each download status */
  @Input() icons?: DownloadButtonIcons;
  /** Whether the button is disabled */
  @Input() disabled: boolean = true;

  /** Delegate invoked by Download button */
  @Output() start = new EventEmitter<void>();

  private readonly defaultLabels: Record<DownloadStatus, string> = {
    [DownloadStatus.Idle]: 'Start',
    [DownloadStatus.Running]: 'Running...',
    [DownloadStatus.Success]: 'Done',
    [DownloadStatus.Error]: 'Retry'
  };

  /** Whether a download is currently in progress */
  get inProgress(): boolean {
    return this.status == DownloadStatus.Running;
  }

  /** Label to display on the button based on the current status */
  get label(): string {
    return this.labels?.[this.status]
        ?? this.defaultLabels[this.status];
  }

  /** Icon URL to display on the button based on the current status */
  get iconUrl(): string | undefined {
    switch (this.status) {
      case DownloadStatus.Running: return this.icons?.running;
      case DownloadStatus.Success: return this.icons?.success;
      case DownloadStatus.Error: return this.icons?.error;
      default: return this.icons?.idle;
    }
  }

  /** Handle click event */
  onClick(): void {
    if (!this.disabled && !this.inProgress) {
      this.start.emit();
    }
  }
}
